import type { HTMLAttributes } from "react";

export interface ProgressProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  label: string;
  max?: number;
  size?: "md" | "sm";
  tone?: "accent" | "success";
  value?: number | null;
}

const defaultProgressMax = 100;

function normalizeMax(max: number | undefined): number {
  if (!Number.isFinite(max) || max === undefined || max <= 0) {
    return defaultProgressMax;
  }

  return max;
}

function clampValue(value: number, max: number): number {
  return Math.min(Math.max(value, 0), max);
}

function progressPercent(value: number, max: number): number {
  return (value / max) * 100;
}

export function Progress({
  className,
  label,
  max,
  size = "md",
  tone = "accent",
  value,
  ...props
}: ProgressProps) {
  const isIndeterminate = value === null || value === undefined;
  const resolvedMax = normalizeMax(max);
  const resolvedValue = isIndeterminate ? null : clampValue(value, resolvedMax);
  const fillWidth =
    resolvedValue === null ? 100 : progressPercent(resolvedValue, resolvedMax);
  const classes = ["marker-progress"];

  if (isIndeterminate) {
    classes.push("marker-progress--indeterminate");
  }
  if (size === "sm") {
    classes.push("marker-progress--size-sm");
  }
  if (tone === "success") {
    classes.push("marker-progress--tone-success");
  }
  if (className) {
    classes.push(className);
  }

  return (
    <div
      {...props}
      aria-label={label}
      aria-valuemax={isIndeterminate ? undefined : resolvedMax}
      aria-valuemin={isIndeterminate ? undefined : 0}
      aria-valuenow={resolvedValue ?? undefined}
      className={classes.join(" ")}
      role="progressbar"
    >
      <svg
        aria-hidden="true"
        className="marker-progress__svg"
        focusable="false"
        preserveAspectRatio="none"
        viewBox="0 0 100 1"
      >
        <rect
          className="marker-progress__track"
          height="1"
          width="100"
          x="0"
          y="0"
        />
        <rect
          className="marker-progress__fill"
          height="1"
          width={fillWidth}
          x="0"
          y="0"
        />
      </svg>
    </div>
  );
}
